import { readTokens } from '@/api/tokenStore';
import { pushWarning } from '@/lib/warningsBuffer';

/**
 * The signal transition as it arrives on the event channel.
 *
 * The whole signal travels in the payload rather than an id, because the network
 * a follow-up request would go over is exactly what may be unavailable during an
 * emergency (D9). `active` is the server's own reading at publish time, so a
 * player does not have to decide whether `expired` means "stop now".
 */
export type SignalEventPayload = {
  signalId: string;
  change: 'raised' | 'extended' | 'cleared' | 'expired';
  kind: 'intermission' | 'delay' | 'emergency';
  scope: 'location' | 'building' | 'organization';
  locationId: string | null;
  buildingId: string | null;
  performanceId: string | null;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  active: boolean;
  messageI18n: Record<string, string> | null;
  screenIds: string[];
};

export type SseHandlers = {
  onScheduleRematerialized(payload: { screenId: string; slotCount: number }): void;
  onPlaylistPublished(playlistId: string): void;
  onLayoutPublished(layoutId: string): void;
  onDeviceRevoked(): void;
  /**
   * A venue signal was raised, extended, cleared or expired for this screen. The
   * server has already filtered by scope, so anything arriving here concerns us.
   */
  onSignalChanged(payload: SignalEventPayload): void;
  /**
   * A remote command for this device's player was issued. Only a wake-up (D7):
   * the payload is not the command, so the handler fetches pending commands.
   */
  onDeviceCommand?(payload: DeviceCommandEventPayload): void;
  /**
   * The stream (re)connected. Fires on the first connect and every reconnect,
   * because pub/sub does not replay what a dropped stream missed — a command
   * issued during the gap is only found by asking.
   */
  onOpen?(): void;
};

export type DeviceCommandEventPayload = {
  commandId: string;
  deviceId: string;
  screenId: string | null;
  kind: string;
  executor: 'player' | 'agent';
};

export function connectPlayerSse(handlers: SseHandlers): () => void {
  let source: EventSource | null = null;
  let closed = false;
  let backoffMs = 2_000;

  const connect = (): void => {
    if (closed) return;
    const tokens = readTokens();
    if (!tokens) return;
    const url = `/api/v1/player/events?access_token=${encodeURIComponent(tokens.deviceToken)}`;
    source = new EventSource(url);

    const handle =
      (eventType: string) =>
      (ev: MessageEvent): void => {
        try {
          const env = JSON.parse(ev.data) as {
            type: string;
            payload: Record<string, unknown>;
          };
          if (eventType === 'schedule-rematerialized') {
            handlers.onScheduleRematerialized(
              env.payload as { screenId: string; slotCount: number },
            );
          } else if (eventType === 'approval-state-changed') {
            const kind = env.payload.entityKind;
            const id = String(env.payload.entityId ?? '');
            if (env.payload.toState !== 'published' || !id) return;
            if (kind === 'playlist') handlers.onPlaylistPublished(id);
            else if (kind === 'layout') handlers.onLayoutPublished(id);
          } else if (eventType === 'device-revoked') {
            handlers.onDeviceRevoked();
          } else if (eventType === 'location-signal-changed') {
            handlers.onSignalChanged(env.payload as unknown as SignalEventPayload);
          } else if (eventType === 'device-command-issued') {
            handlers.onDeviceCommand?.(env.payload as unknown as DeviceCommandEventPayload);
          }
        } catch {
          pushWarning(`sse-parse-failed:${eventType}`);
        }
      };

    source.addEventListener('schedule-rematerialized', handle('schedule-rematerialized'));
    source.addEventListener('approval-state-changed', handle('approval-state-changed'));
    source.addEventListener('device-revoked', handle('device-revoked'));
    source.addEventListener('location-signal-changed', handle('location-signal-changed'));
    source.addEventListener('device-command-issued', handle('device-command-issued'));

    source.addEventListener('open', () => {
      backoffMs = 2_000;
      handlers.onOpen?.();
    });

    source.addEventListener('error', () => {
      pushWarning(`sse-disconnect`);
      source?.close();
      source = null;
      if (closed) return;
      const delay = Math.min(backoffMs, 60_000);
      backoffMs = Math.min(backoffMs * 2, 60_000);
      setTimeout(connect, delay);
    });
  };

  connect();

  return () => {
    closed = true;
    source?.close();
    source = null;
  };
}
